import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth-context";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Recharge from "./pages/Recharge";
import Wallet from "./pages/Wallet";
import Users from "./pages/Users";
import RechargeHistory from "./pages/RechargeHistory";
import Commissions from "./pages/Commissions";
import Settings from "./pages/Settings";
import Reports from "./pages/Reports";
import Profile from "./pages/Profile";
import BankManager from "./pages/BankManager";
import NewsManager from "./pages/NewsManager";
import AdminSettings from "./pages/AdminSettings";
import CreditDebit from "./pages/CreditDebit";
import WhatsAppApiManager from "./pages/WhatsAppApiManager";
import WhatsAppSettings from "./pages/WhatsAppSettings";
import RechargeApiManager from "./pages/RechargeApiManager";
import OperatorCodesManager from "./pages/OperatorCodesManager";
import CommissionPackages from "./pages/CommissionPackages";
import CommissionChart from "./pages/CommissionChart";
import OperatorCommissions from "./pages/OperatorCommissions";
import CommissionRanges from "./pages/CommissionRanges";
import Complaints from "./pages/Complaints";
import AmountRanges from "./pages/AmountRanges";
import InvalidAmounts from "./pages/InvalidAmounts";
import UserOperatorBlocks from "./pages/UserOperatorBlocks";
import SuperDistributors from "./pages/SuperDistributors";
import ApiUsers from "./pages/ApiUsers";
import Services from "./pages/Services";
import BillPayment from "./pages/BillPayment";
import BillOperators from "./pages/BillOperators";
import BannerManager from "./pages/BannerManager";
import ApiConfigManager from "./pages/ApiConfigManager";
import ChangeApi from "./pages/ChangeApi";
import PlanApiManager from "./pages/PlanApiManager";
import ServerInfo from "./pages/ServerInfo";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/" element={<Landing />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Protected Routes - All Users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bill-payment"
        element={
          <ProtectedRoute allowedRoles={["admin", "retailer", "distributor"]}>
            <BillPayment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commissions"
        element={
          <ProtectedRoute>
            <Commissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      {/* Retailer & Distributor Routes */}
      <Route
        path="/recharge"
        element={
          <ProtectedRoute allowedRoles={["admin", "retailer", "distributor"]}>
            <Recharge />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute allowedRoles={["retailer", "distributor"]}>
            <RechargeHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recharge-management"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RechargeHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/banks"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <BankManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <NewsManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/credit-debit"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CreditDebit />
          </ProtectedRoute>
        }
      />

      {/* Admin & Distributor Routes */}
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/retailers"
        element={
          <ProtectedRoute allowedRoles={["distributor"]}>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer"
        element={
          <ProtectedRoute allowedRoles={["admin", "distributor"]}>
            <Wallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recharges"
        element={
          <ProtectedRoute allowedRoles={["admin", "distributor"]}>
            <RechargeHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={["admin", "distributor"]}>
            <Reports />
          </ProtectedRoute>
        }
      />

      {/* Phase 2: Admin Manager Pages */}
      <Route
        path="/whatsapp-api"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WhatsAppApiManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp-settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <WhatsAppSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recharge-api"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <RechargeApiManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operator-codes"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <OperatorCodesManager />
          </ProtectedRoute>
        }
      />

      {/* Phase 3: Commission Management Pages */}
      <Route
        path="/commission-packages"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CommissionPackages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commission-chart"
        element={
          <ProtectedRoute allowedRoles={["admin", "retailer", "distributor", "super_distributor"]}>
            <CommissionChart />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operator-commissions"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <OperatorCommissions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/commission-ranges"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CommissionRanges />
          </ProtectedRoute>
        }
      />

      {/* Complaints */}
      <Route
        path="/complaints"
        element={
          <ProtectedRoute allowedRoles={["admin", "retailer", "distributor"]}>
            <Complaints />
          </ProtectedRoute>
        }
      />

      {/* Phase 3 P1: Amount & Blocking Controls */}
      <Route
        path="/amount-ranges"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AmountRanges />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invalid-amounts"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <InvalidAmounts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-operator-blocks"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UserOperatorBlocks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bill-operators"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <BillOperators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/banner-manager"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <BannerManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-config"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ApiConfigManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/change-api"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ChangeApi />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plan-api-manager"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <PlanApiManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/server-info"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ServerInfo />
          </ProtectedRoute>
        }
      />

      {/* Phase 4: User Management Pages */}
      <Route
        path="/super-distributors"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SuperDistributors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ApiUsers />
          </ProtectedRoute>
        }
      />

      {/* Admin Only Routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Fallback redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
